// Changing your own password (D48), shared by the Account page and the choose-a-password page
// an account with a temporary password is held on. A change ends every session of the user;
// the API answers with a fresh cookie for this browser.
import { CheckCircle } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";
import { Button, Password } from "rizzui";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { ErrorState } from "../ui/states";
import { changePassword, MIN_PASSWORD_CHARS, passwordProblem, strength } from "./api";

function StrengthMeter({ password }: { password: string }) {
  const t = useT().auth.password;
  const s = strength(password);
  const verdict = s === 0 ? t.hintTooShort(MIN_PASSWORD_CHARS) : s >= 3 ? t.strong : t.fair;
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="grid grid-cols-4 gap-1.5" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={cn("h-1.5 rounded-full", i < s ? (s >= 3 ? "bg-green" : "bg-orange") : "bg-gray-200")} />
        ))}
      </div>
      <div className="mt-1.5 text-xs text-gray-500">{verdict} · {t.checkedLocally}</div>
    </div>
  );
}

export function PasswordForm({ onChanged, submitLabel }: { onChanged: () => void | Promise<void>; submitLabel?: string }) {
  const t = useT().auth.password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const matches = repeat !== "" && next === repeat;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const problem = passwordProblem(next, repeat);
    if (problem) return setError(problem);
    setBusy(true);
    setError(undefined);
    try {
      await changePassword(current, next);
      setCurrent("");
      setNext("");
      setRepeat("");
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-4" aria-label={t.change} onSubmit={(e) => void onSubmit(e)}>
      <Password label={t.current} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required labelClassName="font-medium" />
      <div>
        <Password label={t.new} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required labelClassName="font-medium" />
        <StrengthMeter password={next} />
      </div>
      <Password label={t.confirm} autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required labelClassName="font-medium" />
      {matches ? (
        <div className="flex items-center gap-2 text-xs text-green-dark">
          <CheckCircle weight="fill" className="h-4 w-4" /> {t.matches}
        </div>
      ) : null}
      {error ? <ErrorState title={t.notChanged} message={error} /> : null}
      <div className="flex gap-3 pt-1">
        <Button type="submit" isLoading={busy}>
          {submitLabel ?? t.update}
        </Button>
      </div>
    </form>
  );
}
